import { trace } from "firebase/performance";

import { getPerformanceInstance } from "../firebase";
import { Config, SystemInformation } from "../types/Config";
import { throwOnFailure } from "../utils/http";
import { getEzioId } from "../utils/id";

export class ConfigClient {
    constructor(private client: typeof fetch) {}

    public async setup(): Promise<Config> {
        const t = trace(getPerformanceInstance(), "setup");
        t.start();
        const response = await this.client("/ezio/v1/setup", {
            method: "GET",
            headers: { version: import.meta.env.VITE_VERSION }
        })
            .then((r) => {
                if (r.status === 404)
                    location.href = `${
                        import.meta.env.VITE_RETO_ADMINUI_URL
                    }/onboarding/staged-devices/new?id=${getEzioId()}&type=sco`;

                return r;
            })
            .then((res) => throwOnFailure(res));
        t.stop();

        return await response.json();
    }

    public async systemInformation(): Promise<SystemInformation> {
        const t = trace(getPerformanceInstance(), "config-system-information");
        t.start();
        const response = await this.client(`/ezio/v1/system-information`, {
            method: "GET"
        });
        t.stop();

        return await response.json();
    }

    public async setStatus({ isEnabled }: { isEnabled: boolean }) {
        await this.client("/ezio/v1/status", {
            method: "PUT",
            body: JSON.stringify({ isEnabled })
        });
    }

    public async setCartCheck({ isEnabled }: { isEnabled: boolean }) {
        await this.client(`/ezio/v1/store-cart-check`, {
            method: "PUT",
            body: JSON.stringify({ isEnabled })
        });
    }

    public async paymentDeviceLogin() {
        const response = await this.client(`/ezio/v1/payment-device-login`, {
            method: "POST"
        });

        if (!response.ok)
            throw new Error(await this.readError(response, "Login failed"));
    }

    public async probePaymentTerminal() {
        const response = await this.client(`/ezio/v1/payment-terminal-probe`, {
            method: "POST"
        });

        if (!response.ok)
            throw new Error(await this.readError(response, "Probe failed"));
    }

    public async updatePaymentDeviceIp(ip: string) {
        const response = await this.client(`/ezio/v1/payment-device-ip`, {
            method: "PUT",
            body: JSON.stringify({ ip })
        });

        if (!response.ok)
            throw new Error(
                await this.readError(response, "Failed to update IP")
            );
    }

    private async readError(
        response: Response,
        fallback: string
    ): Promise<string> {
        const body = await response.text();

        return body || fallback;
    }
}
